"use client"

import Image from "next/image"
import { useState } from "react"
import { ArrowRight, BarChart3, BriefcaseBusiness, ClipboardCheck, UsersRound } from "lucide-react"

interface ConsultingScene {
  label: string
  src: string
  alt: string
}

interface DecisionInstrumentIslandProps {
  scenes: ConsultingScene[]
}

const rooms = [
  {
    key: "client",
    label: "Client",
    position: "top-[11%] left-[16%]",
    title: "Client room",
    memo: "Map the buying journey, trust gaps, service expectations, and the reason the right client hesitates.",
    artifact: "Client journey map",
    icon: UsersRound,
  },
  {
    key: "leadership",
    label: "Leader",
    position: "top-[18%] right-[10%]",
    title: "Leadership room",
    memo: "Clarify the story around the choice leadership needs to make, not every priority the firm can name.",
    artifact: "Leadership decision packet",
    icon: BriefcaseBusiness,
  },
  {
    key: "delivery",
    label: "Delivery",
    position: "bottom-[17%] left-[10%]",
    title: "Delivery room",
    memo: "Find where growth is being subsidized by exceptions, senior attention, and undocumented client promises.",
    artifact: "Delivery operating map",
    icon: ClipboardCheck,
  },
  {
    key: "commercial",
    label: "Growth",
    position: "bottom-[10%] right-[17%]",
    title: "Growth room",
    memo: "Turn positioning and service choices into thresholds, tradeoffs, costs, and next-step evidence.",
    artifact: "Growth decision memo",
    icon: BarChart3,
  },
]

export function DecisionInstrumentIsland({ scenes }: DecisionInstrumentIslandProps) {
  const [activeKey, setActiveKey] = useState(rooms[0].key)
  const activeRoom = rooms.find((room) => room.key === activeKey) ?? rooms[0]
  const ActiveIcon = activeRoom.icon
  const activeScene = scenes[rooms.findIndex((room) => room.key === activeKey) % scenes.length] ?? scenes[0]

  return (
    <div className="decision-instrument relative mx-auto aspect-square w-full max-w-[44rem]">
      <div className="absolute inset-0 rounded-full border border-white/12 bg-[radial-gradient(circle_at_center,rgba(219,255,74,0.16),rgba(16,17,15,0.08)_34%,rgba(255,255,255,0.03)_35%,transparent_64%)]" />
      <div className="instrument-ring absolute inset-[7%] rounded-full border border-accent/35" />
      <div className="instrument-ring instrument-ring-reverse absolute inset-[17%] rounded-full border border-secondary/35" />
      <div className="absolute inset-[29%] rounded-full border border-white/18 bg-primary/80 backdrop-blur-xl" />

      <div className="absolute left-[4%] top-[3%] z-20 hidden w-40 overflow-hidden rounded-[1.5rem] border border-white/16 bg-white/[0.07] p-1 shadow-2xl backdrop-blur sm:block xl:w-48">
        <div className="relative aspect-[4/3] overflow-hidden rounded-[1.15rem]">
          <Image src={activeScene.src} alt={activeScene.alt} fill sizes="12rem" className="object-cover transition-transform duration-700 hover:scale-110" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(16,17,15,0.78))]" />
          <p className="absolute inset-x-3 bottom-3 font-mono text-[0.58rem] font-black uppercase leading-4 tracking-[0.18em] text-white">
            {activeRoom.artifact}
          </p>
        </div>
      </div>

      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 600 600" aria-hidden="true">
        <path
          d="M140 176 L477 438 M105 410 L496 210 M300 54 L300 546"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="1"
          strokeDasharray="8 12"
        />
      </svg>

      {rooms.map((room) => (
        <button
          key={room.key}
          type="button"
          aria-pressed={activeKey === room.key}
          data-active={activeKey === room.key ? "true" : "false"}
          onClick={() => setActiveKey(room.key)}
          className={`instrument-node instrument-button absolute ${room.position} z-10 flex size-16 items-center justify-center rounded-full border text-center font-mono text-[0.52rem] font-black uppercase tracking-[0.14em] backdrop-blur transition duration-300 sm:size-24 sm:text-[0.66rem] sm:tracking-[0.2em]`}
        >
          {room.label}
        </button>
      ))}

      <div className="absolute left-1/2 top-1/2 w-[12.5rem] -translate-x-1/2 -translate-y-1/2 text-center sm:w-[17rem]">
        <p className="font-mono text-[0.55rem] font-black uppercase tracking-[0.2em] text-accent sm:text-[0.66rem] sm:tracking-[0.28em]">
          Choose a lens
        </p>
        <div className="mx-auto mt-3 grid size-9 place-items-center rounded-full border border-white/18 bg-white/[0.07] sm:mt-4 sm:size-11">
          <ActiveIcon className="size-4 text-accent sm:size-5" />
        </div>
        <p className="mt-3 font-serif text-2xl font-black leading-none text-white sm:mt-4 sm:text-4xl">
          {activeRoom.title}
        </p>
        <p className="mt-3 text-xs leading-5 text-white/62 sm:mt-4 sm:text-sm sm:leading-6">
          {activeRoom.memo}
        </p>
        <p className="mx-auto mt-5 hidden items-center gap-2 rounded-full border border-white/14 px-3 py-2 font-mono text-[0.58rem] font-black uppercase tracking-[0.16em] text-white/74 sm:inline-flex">
          Reveal: {activeRoom.artifact}
          <ArrowRight className="size-3" />
        </p>
      </div>
    </div>
  )
}
